import type { CreateInviteRequest, Invite } from '@mcgames/contracts';
import { Badge, Button, Select, Spinner, TextInput } from '@mcgames/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiMutation, useApiQuery } from '../api/hooks.ts';
import { formatDateTime } from '../lib/format-date.ts';
import styles from './Admin.module.css';
import { ConfirmDialog } from './ConfirmDialog.tsx';
import { ErrorText } from './games/ErrorText.tsx';

const INVITES_PATH = '/api/admin/invites';

const EXPIRY_OPTIONS = [
  { value: '24', hours: 24, key: 'h24' },
  { value: '168', hours: 168, key: 'd7' },
  { value: '720', hours: 720, key: 'd30' },
] as const;
type ExpiryValue = (typeof EXPIRY_OPTIONS)[number]['value'];

type InviteState = 'open' | 'used' | 'expired';

function inviteState(invite: Invite, now: number): InviteState {
  if (invite.usedAt !== null) return 'used';
  return new Date(invite.expiresAt).getTime() <= now ? 'expired' : 'open';
}

export function InvitesSection() {
  const { t } = useTranslation('admin');
  const invites = useApiQuery<Invite[]>(INVITES_PATH);

  return (
    <section className={styles['section']} aria-labelledby="invites-title">
      <h2 id="invites-title" className={styles['subheading']}>
        {t('invites.title')}
      </h2>
      <p className={styles['muted']}>{t('invites.intro')}</p>
      <CreateInvite />
      {invites.isPending && <Spinner />}
      {invites.isError && (
        <p role="alert" className={styles['error']}>
          {t('invites.loadFailed')}
        </p>
      )}
      {invites.data?.length === 0 && <p className={styles['muted']}>{t('invites.empty')}</p>}
      {invites.data !== undefined && invites.data.length > 0 && (
        <InvitesTable invites={invites.data} />
      )}
    </section>
  );
}

function CreateInvite() {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();
  const [expiry, setExpiry] = useState<ExpiryValue>('168');
  const [created, setCreated] = useState<Invite | null>(null);
  const create = useApiMutation<CreateInviteRequest, Invite>('POST', INVITES_PATH, {
    onSuccess: (invite) => {
      setCreated(invite);
      void queryClient.invalidateQueries({ queryKey: [INVITES_PATH] });
    },
  });

  return (
    <div className={styles['card']}>
      <div className={styles['row']}>
        <div className={styles['fieldWidth']}>
          <Select
            label={t('invites.expiry')}
            value={expiry}
            options={EXPIRY_OPTIONS.map((option) => ({
              value: option.value,
              label: t(`invites.expiryOptions.${option.key}`),
            }))}
            onChange={setExpiry}
          />
        </div>
        <Button
          disabled={create.isPending}
          onClick={() => {
            const chosen = EXPIRY_OPTIONS.find((option) => option.value === expiry);
            create.mutate({ expiresInHours: chosen?.hours ?? 168 });
          }}
        >
          {t('invites.create')}
        </Button>
      </div>
      <ErrorText error={create.error} />
      {created !== null && <InviteLink key={created.id} invite={created} />}
    </div>
  );
}

type CopyState = 'idle' | 'copied' | 'failed';

/**
 * Copies `url` to the clipboard. When the clipboard is refused, `onRefused` selects the URL so it
 * can be copied by hand.
 */
function useCopyUrl(url: string, onRefused: () => void) {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState('copied');
    } catch {
      onRefused();
      setCopyState('failed');
    }
  }
  return { copyState, copy };
}

function CopyStatus({ state }: { state: CopyState }) {
  const { t } = useTranslation('admin');
  return (
    <p role="status" className={state === 'failed' ? styles['error'] : styles['status']}>
      {state === 'copied' && t('invites.copied')}
      {state === 'failed' && t('invites.copyFailed')}
    </p>
  );
}

/** The new invite's URL with a copy button; when the clipboard is refused the URL is selected for a manual copy. */
function InviteLink({ invite }: { invite: Invite }) {
  const { t } = useTranslation('admin');
  const field = useRef<HTMLDivElement>(null);
  const { copyState, copy } = useCopyUrl(invite.url, () => {
    const input = field.current?.querySelector('input');
    input?.focus();
    input?.select();
  });

  return (
    <div>
      <div className={styles['row']}>
        <div className={styles['grow']} ref={field}>
          <TextInput
            label={t('invites.linkLabel')}
            value={invite.url}
            readOnly
            onFocus={(event) => event.target.select()}
          />
        </div>
        <Button variant="secondary" onClick={() => void copy()}>
          {t('invites.copy')}
        </Button>
      </div>
      <CopyStatus state={copyState} />
    </div>
  );
}

/** The link of an open invite as selectable text with a copy button (the API returns it for every invite). */
function InviteRowLink({ url, expires }: { url: string; expires: string }) {
  const { t } = useTranslation('admin');
  const text = useRef<HTMLElement>(null);
  const { copyState, copy } = useCopyUrl(url, () => {
    if (text.current) window.getSelection()?.selectAllChildren(text.current);
  });

  return (
    <>
      <code ref={text} className={styles['wrap']}>
        {url}
      </code>
      <div>
        <Button
          size="sm"
          variant="secondary"
          aria-label={t('invites.copyRow', { date: expires })}
          onClick={() => void copy()}
        >
          {t('invites.copy')}
        </Button>
      </div>
      <CopyStatus state={copyState} />
    </>
  );
}

function InvitesTable({ invites }: { invites: Invite[] }) {
  const { t } = useTranslation('admin');
  const now = Date.now();
  return (
    <div className={styles['tableWrap']}>
      <table className={styles['table']} aria-label={t('invites.label')}>
        <thead>
          <tr>
            <th scope="col">{t('invites.status')}</th>
            <th scope="col">{t('invites.expires')}</th>
            <th scope="col">{t('invites.usedBy')}</th>
            <th scope="col">{t('invites.link')}</th>
            <th scope="col">{t('invites.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {invites.map((invite) => (
            <InviteRow key={invite.id} invite={invite} state={inviteState(invite, now)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InviteRow({ invite, state }: { invite: Invite; state: InviteState }) {
  const { t, i18n } = useTranslation('admin');
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const remove = useApiMutation<void>(
    'DELETE',
    `${INVITES_PATH}/${encodeURIComponent(invite.id)}`,
    {
      onSuccess: () => {
        setAsking(false);
        void queryClient.invalidateQueries({ queryKey: [INVITES_PATH] });
      },
    },
  );
  const expires = formatDateTime(invite.expiresAt, i18n.language);

  return (
    <tr>
      <td>
        <Badge tone={state === 'open' ? 'accent' : 'default'}>{t(`invites.state.${state}`)}</Badge>
      </td>
      <td className={styles['nowrap']}>{expires}</td>
      <td className={styles['wrap']}>{invite.usedByUsername ?? ''}</td>
      <td>{state === 'open' && <InviteRowLink url={invite.url} expires={expires} />}</td>
      <td>
        <Button
          size="sm"
          variant="secondary"
          aria-label={t('invites.delete', { date: expires })}
          onClick={() => {
            remove.reset();
            setAsking(true);
          }}
        >
          {t('actions.delete', { ns: 'common' })}
        </Button>
        {asking && (
          <ConfirmDialog
            title={t('invites.deleteTitle')}
            message={t('invites.deleteMessage')}
            confirmLabel={t('invites.deleteConfirm')}
            pending={remove.isPending}
            error={remove.error}
            onConfirm={() => remove.mutate()}
            onCancel={() => setAsking(false)}
          />
        )}
      </td>
    </tr>
  );
}
